import type { Control } from "./Control.js";
import type { Desktop } from "./Desktop.js";
import {
  makeMouseArgs,
  type MouseEventArgs,
  type MouseSource,
  type WheelEventArgs,
} from "./events.js";
import { distance, rectContains, type Point } from "../../common/geometry.js";

const DOUBLE_CLICK_MS = 400;
const DOUBLE_CLICK_SLOP = 4;
/** deltaMode === DOM_DELTA_LINE: convert lines to a sensible pixel amount. */
const LINE_HEIGHT_PX = 16;
/**
 * How far the fingers must change their separation before a two-finger drag
 * is also treated as a pinch. Without it every pan zooms a little, because
 * two fingers never move in perfect parallel.
 */
const PINCH_SLOP = 10;
/**
 * How far one finger must travel before the drag is taken as a scroll rather
 * than a press. Small enough not to feel sticky, large enough that a tap with
 * a wobble in it still lands as a tap.
 */
const TOUCH_SLOP = 8;

/** See `InputManager.touchClaim`. */
export type TouchClaim = "deciding" | "claimed" | "declined";
/**
 * How long one finger must rest before the press becomes a context menu.
 * The figure both phone platforms use, and necessarily an outcome of holding
 * still: a finger that travels `TOUCH_SLOP` first is scrolling instead.
 */
const LONG_PRESS_MS = 500;
/** Weight of the newest sample in the velocity estimate, 0..1. */
const VELOCITY_SMOOTHING = 0.7;
/** A longer gap between samples than this means the finger stopped. */
const MAX_SAMPLE_GAP = 0.1;
const PAGE_HEIGHT_PX = 400;

/**
 * The same press, reported as the secondary button.
 *
 * A touch contact is always button 0, so a long press has to say what it
 * means some other way. Rather than fake a DOM event, the press is described
 * by the handful of fields a `MouseEventArgs` is actually built from.
 */
function secondaryOf(e: PointerEvent): MouseSource {
  return {
    button: 2,
    buttons: 2,
    ctrlKey: e.ctrlKey,
    shiftKey: e.shiftKey,
    altKey: e.altKey,
    metaKey: e.metaKey,
  };
}

/**
 * Translates DOM pointer, wheel and mouse events into Webwin control events.
 *
 * Routing order is: an explicit Webwin capture (set during a drag) first, then
 * a hit test through the desktop honoring the modal stack.
 */
export class InputManager {
  #desktop: Desktop;
  #canvas: HTMLCanvasElement;

  #captured: Control | null = null;
  #hovered: Control | null = null;
  #pressedControl: Control | null = null;
  #pressedButton = -1;
  /** Where that press landed, so a drag can be measured against it. */
  #pressedAt: Point = { x: 0, y: 0 };

  #lastClickAt = 0;
  #lastClickPos: Point = { x: 0, y: 0 };
  #lastClickTarget: Control | null = null;
  #clickCount = 0;

  /** Last pointer position in canvas coordinates. */
  #pointer: Point = { x: 0, y: 0 };
  #inside = false;

  /** Every contact currently down, in canvas coordinates, by pointerId. */
  #contacts = new Map<number, Point>();
  /**
   * Which contacts have reported a new position since the gesture was last
   * measured.
   *
   * Fingers move one `pointermove` at a time, never together, so measuring on
   * every event sees the pair stretch and snap back between updates. Over a
   * fast drag that transient is large enough to look like a pinch. Waiting
   * for a complete round means every measurement is of a real hand position.
   */
  #moved = new Set<number>();
  /** The in-flight two-finger gesture, if any. */
  #pan: {
    target: Control | null;
    /** Centroid at the last update. */
    last: Point;
    /** Finger separation at the last update, and when the gesture began. */
    spread: number;
    startSpread: number;
    /** True once the separation has changed enough to mean a pinch. */
    zooming: boolean;
    /** Smoothed scroll speed, content px per second, for the throw. */
    vx: number;
    vy: number;
    at: number;
    /**
     * True when a mouse is dragging rather than a finger. Such a pan stays
     * inside the controls that asked for one, and never coasts: a mouse that
     * has stopped moving is a hand that has stopped, not a flick.
     */
    mouse: boolean;
  } | null = null;
  /**
   * A touch press held back until it is clear a second finger is not coming.
   *
   * Without this the first finger of a two-finger scroll lands as a real
   * press, and panning a list changes its selection on the way past. The
   * delay is one input event, which is a few milliseconds — the control still
   * sees pointerdown before pointermove, just slightly later.
   */
  #pendingDown: PointerEvent | null = null;
  /** Where that press landed, so a move can be measured against it. */
  #pendingAt: Point = { x: 0, y: 0 };
  /** The timer that would turn that held-back press into a context menu. */
  #longPressTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * True from the moment a long press has delivered its menu until the finger
   * that made it lifts, so that lift is not also read as a click.
   */
  #longPressed = false;
  #touchClaim: TouchClaim | null = null;

  /**
   * How far, in UI units, a finger travels before a touch is a slide rather
   * than a press - and so before `touchClaim` is decided.
   *
   * A page sharing touches with a pinch-zoomed browser sets it: the browser
   * commits to its own pan at a slop measured on the *screen*, which at 3x
   * zoom is a third of a UI unit per dip, and this has to be decided first.
   */
  touchSlop = TOUCH_SLOP;

  /**
   * Hand a declined touch to the browser outright: the held-back press is
   * dropped rather than delivered, and the rest of that finger's movement is
   * ignored. For a page whose `touch-action` lets the browser pan - the
   * browser takes a slide the application did not want, and a press
   * delivered in the moment before it does (focus moving, a list starting a
   * selection) is the application acting on a gesture that was never its.
   * Off by default: without a browser to hand it to, a declined slide is
   * still a press, and drag-selecting a list is how it is used.
   */
  yieldDeclinedTouches = false;

  /**
   * Whether the one-finger touch in progress is the application's.
   *
   * Decided the moment the finger has moved far enough to mean it, by the
   * same rule that turns a slide into a scroll: `claimed` when something under
   * it took the movement or keeps its own drag (`handlesDragAt`), or a drag
   * session is under way; `declined` when nothing wanted it. Null with no
   * finger down.
   *
   * For a page that shares touches with the browser. Chromium dispatches a
   * `pointermove` before the `touchmove` it came from, so a non-passive
   * `touchmove` listener can read this and cancel the browser's own pan
   * exactly when the application has the gesture - which is how a zoomed-in
   * page lets a finger move a window, and pan the view everywhere else.
   */
  get touchClaim(): TouchClaim | null {
    return this.#touchClaim;
  }

  constructor(desktop: Desktop, canvas: HTMLCanvasElement) {
    this.#desktop = desktop;
    this.#canvas = canvas;

    // Without this the browser claims two-finger gestures for page scroll and
    // pinch-zoom, and the second contact never reaches us at all. Set here so
    // an embedder does not have to know; a page that has already chosen keeps
    // its value.
    if (!canvas.style.touchAction) canvas.style.touchAction = "none";

    canvas.addEventListener("pointerdown", this.#onPointerDown);
    canvas.addEventListener("pointermove", this.#onPointerMove);
    canvas.addEventListener("pointerup", this.#onPointerUp);
    canvas.addEventListener("pointercancel", this.#onPointerCancel);
    canvas.addEventListener("pointerleave", this.#onPointerLeave);
    canvas.addEventListener("contextmenu", this.#onContextMenu);
    // Non-passive: ctrl+wheel must be preventDefault-ed or the browser zooms.
    canvas.addEventListener("wheel", this.#onWheel, { passive: false });
  }

  get pointerPosition(): Point {
    return this.#pointer;
  }

  get pointerInside(): boolean {
    return this.#inside;
  }

  get hoveredControl(): Control | null {
    return this.#hovered;
  }

  get capturedControl(): Control | null {
    return this.#captured;
  }

  /** True while a two-finger pan is in progress. */
  get panning(): boolean {
    return this.#pan !== null;
  }

  /** True once a two-finger gesture has become a pinch as well as a pan. */
  get pinching(): boolean {
    return this.#pan?.zooming ?? false;
  }

  /** The control a two-finger pan is scrolling from, if any. */
  get panTarget(): Control | null {
    return this.#pan?.target ?? null;
  }

  /** How many contacts are currently down. */
  get contactCount(): number {
    return this.#contacts.size;
  }

  /**
   * Route all further pointer events to `control` until released. Used by
   * title-bar drags, scrollbar thumbs, and text selection.
   */
  setCapture(control: Control): void {
    this.#captured = control;
  }

  releaseCapture(control?: Control): void {
    if (control && this.#captured !== control) return;
    this.#captured = null;
    this.#updateHover(this.#pointer);
  }

  /** Drop any state referring to `control` — called when it is removed. */
  forget(control: Control): void {
    if (this.#captured && control.contains(this.#captured)) this.#captured = null;
    if (this.#hovered && control.contains(this.#hovered)) {
      this.#hovered.setHovered(false);
      this.#hovered = null;
    }
    if (this.#pressedControl && control.contains(this.#pressedControl)) {
      this.#pressedControl.setPressed(false);
      this.#pressedControl = null;
    }
    if (this.#lastClickTarget && control.contains(this.#lastClickTarget)) {
      this.#lastClickTarget = null;
    }
    if (this.#pan?.target && control.contains(this.#pan.target)) this.#pan.target = null;
  }

  // --- coordinate helpers ------------------------------------------------

  /**
   * A DOM event's position in the desktop's own units — CSS pixels relative
   * to the canvas, divided by the UI scale. The counterpart of the transform
   * `Desktop.render` pushes, and the only other place the scale is known.
   */
  #toCanvas(e: { clientX: number; clientY: number }): Point {
    const rect = this.#canvas.getBoundingClientRect();
    const scale = this.#desktop.uiScale || 1;
    return { x: (e.clientX - rect.left) / scale, y: (e.clientY - rect.top) / scale };
  }

  /** The control that should receive a pointer event at canvas point `p`. */
  #target(p: Point): Control | null {
    return this.#captured ?? this.#desktop.hitTestScreen(p);
  }

  /**
   * True when canvas point `p` falls under a busy container's scrim.
   *
   * Checked here rather than in every control, because otherwise each one
   * would have to remember: a busy ListBox would still be selecting rows
   * under its own spinner. The walk is up the ancestors because the busy
   * container is usually a pane above whatever was hit.
   *
   * A container decides how much of itself the scrim covers — `Window`
   * excludes its caption — so a busy window can still be moved and closed.
   */
  #underBusyScrim(target: Control, p: Point): boolean {
    for (let c: Control | null = target; c; c = c.parent) {
      const r = c.inputBlockRect;
      if (r && rectContains(r, c.fromScreen(p))) return true;
    }
    return false;
  }

  #argsFor(control: Control, p: Point, e: MouseSource, clickCount = 1): MouseEventArgs {
    const local = control.fromScreen(p);
    return makeMouseArgs(local.x, local.y, p, e, clickCount);
  }

  // --- two-finger pan -----------------------------------------------------

  /**
   * How far apart the contacts are: for the usual two, the distance between
   * them. Written as twice the mean distance from the centroid so a third
   * finger joining does not make it meaningless.
   */
  #spread(): number {
    const n = this.#contacts.size;
    if (n < 2) return 0;
    const c = this.#centroid();
    let total = 0;
    for (const p of this.#contacts.values()) total += distance(p, c);
    return (total / n) * 2;
  }

  /** Midpoint of every contact currently down. */
  #centroid(): Point {
    let x = 0;
    let y = 0;
    for (const c of this.#contacts.values()) {
      x += c.x;
      y += c.y;
    }
    const n = this.#contacts.size;
    return n === 0 ? { x: 0, y: 0 } : { x: x / n, y: y / n };
  }

  /**
   * A second contact turns the gesture into a pan.
   *
   * The press already in flight is abandoned rather than completed: the user
   * put a second finger down, so they meant to scroll, not to click whatever
   * the first finger happened to land on.
   */
  #beginPan(): void {
    const target = this.#pressedControl ?? this.#desktop.hitTestScreen(this.#centroid());
    if (this.#pressedControl) {
      this.#pressedControl.setPressed(false);
      this.#pressedControl = null;
    }
    this.#captured = null;
    this.#desktop.tooltips.hide();
    const spread = this.#spread();
    this.#pan = {
      target,
      last: this.#centroid(),
      spread,
      startSpread: spread,
      zooming: false,
      vx: 0,
      vy: 0,
      at: performance.now(),
      mouse: false,
    };
  }

  /**
   * Re-baseline after the number of contacts changes.
   *
   * A finger joining or leaving moves the centroid and the spread without the
   * hand having moved, so both are re-read rather than measured against the
   * old ones — otherwise the content jumps.
   */
  #rebasePan(): void {
    const pan = this.#pan;
    if (!pan) return;
    this.#moved.clear();
    pan.last = this.#centroid();
    pan.spread = this.#spread();
    if (!pan.zooming) pan.startSpread = pan.spread;
  }

  /** Offer the release up the same chain `panBy` uses. True once claimed. */
  #offerPanEnd(pan: { target: Control | null; vx: number; vy: number; mouse: boolean }): boolean {
    for (let c: Control | null = pan.target; c; c = c.parent) {
      if (!c.effectivelyEnabled) continue;
      if (pan.mouse && !c.pansWithMouse) continue;
      if (c.panEnd(pan.vx, pan.vy)) return true;
    }
    return false;
  }

  #endPan(): void {
    const pan = this.#pan;
    if (!pan) return;
    // Let go of a moving list and it carries on, the way a phone does. A
    // pinch is excluded: nobody expects a zoom to coast, and so is a control
    // that claims the release through `panEnd` — one that snaps to a resting
    // place is finishing the gesture itself and a fling would fight it.
    if (pan.target && !pan.zooming && !this.#offerPanEnd(pan)) {
      if (!pan.mouse) this.#desktop.startFling(pan.target, pan.vx, pan.vy);
    }
    // Lifting one of two fingers leaves the other resting on the glass; it
    // must not become a fresh press, or letting go clicks whatever is under it.
    this.#pan = null;
    this.#moved.clear();
    this.#pressedControl = null;
    this.#lastClickTarget = null;
  }

  /**
   * Move the pan on, scrolling whatever under the fingers will take it.
   *
   * The content follows the fingers, so the scroll offset moves the opposite
   * way. Leftover travel chains to the parent, and the walk stops at the
   * first ancestor that takes anything on an axis rather than splitting one
   * gesture across two scrollers.
   */
  #flushGesture(): void {
    this.#moved.clear();
    this.#updatePan();
  }

  #updatePan(): void {
    const pan = this.#pan;
    if (!pan) return;
    const now = this.#centroid();
    const dx = pan.last.x - now.x;
    const dy = pan.last.y - now.y;
    pan.last = now;
    this.#trackVelocity(pan, dx, dy);

    // Zoom before pan: the anchor is where the fingers are now, and scaling
    // first means the pan that follows is measured against the new scale.
    this.#applyPinch(now);
    this.#applyPan(dx, dy);
  }

  /**
   * Scale the content if the fingers are spreading or closing.
   *
   * Nothing happens until the separation has moved past `PINCH_SLOP`, so a
   * plain two-finger scroll does not creep in scale. Once it has, the gesture
   * stays a pinch for as long as it lasts — going back under the threshold
   * mid-pinch would be a visible stutter.
   */
  #applyPinch(at: Point): void {
    const pan = this.#pan;
    if (!pan) return;
    const spread = this.#spread();
    if (spread <= 0 || pan.spread <= 0) return;

    if (!pan.zooming) {
      if (Math.abs(spread - pan.startSpread) < PINCH_SLOP) return;
      pan.zooming = true;
      // Measure the first step from here, so crossing the threshold does not
      // apply the whole slop as one jump.
      pan.spread = spread;
      return;
    }

    const factor = spread / pan.spread;
    pan.spread = spread;
    if (factor === 1) return;
    for (let c: Control | null = pan.target; c; c = c.parent) {
      if (c.effectivelyEnabled && c.zoomBy(factor, c.fromScreen(at))) return;
    }
  }

  #trackVelocity(pan: { vx: number; vy: number; at: number }, dx: number, dy: number): void {
    const now = performance.now();
    const dt = (now - pan.at) / 1000;
    pan.at = now;
    pan.vx = smoothVelocity(pan.vx, dx, dt);
    pan.vy = smoothVelocity(pan.vy, dy, dt);
  }

  /** Returns the travel nothing took. */
  #applyPan(dx: number, dy: number): Point {
    const pan = this.#pan;
    if (!pan || (dx === 0 && dy === 0)) return { x: dx, y: dy };
    let rx = dx;
    let ry = dy;
    for (let c: Control | null = pan.target; c; c = c.parent) {
      if (!c.effectivelyEnabled) continue;
      // A mouse drag chains only through controls that asked for one, or a
      // carousel dragged past its last page would scroll the pane around it.
      if (pan.mouse && !c.pansWithMouse) continue;
      const used = c.panBy(rx, ry);
      rx -= used.x;
      ry -= used.y;
      if (rx === 0 && ry === 0) break;
    }
    return { x: rx, y: ry };
  }

  // --- pointer -----------------------------------------------------------

  /**
   * Turn a held-back touch press into a one-finger scroll, if that is what it
   * has turned out to be.
   *
   * Phones scroll with one finger, and that is worth more than drag-selecting
   * a list. The test is simply whether anything under the finger takes the
   * movement: `panBy` reports what it consumed, so a scrollable already at its
   * end declines and the press goes through as normal. Controls whose whole
   * purpose is the drag — a scroll bar thumb, a splitter, a title bar, a
   * column divider — say so through `handlesDragAt` and are left alone.
   */
  #claimAsScroll(p: Point): boolean {
    const from = this.#pendingAt;
    if (distance(p, from) < this.touchSlop) return true; // still deciding; swallow

    const target = this.#desktop.hitTestScreen(from);
    if (!target) return false;
    if (target.handlesDragAt(target.fromScreen(from))) {
      this.#touchClaim = "claimed";
      return false;
    }
    if (this.#underBusyScrim(target, from)) return false;

    // The content follows the finger, so the scroll goes the other way.
    const dx = from.x - p.x;
    const dy = from.y - p.y;
    for (let c: Control | null = target; c; c = c.parent) {
      if (!c.effectivelyEnabled) continue;
      const used = c.panBy(dx, dy);
      if (used.x === 0 && used.y === 0) continue;
      // Something moved: this is a scroll. The press never happened.
      this.#pendingDown = null;
      this.#cancelLongPress();
      this.#desktop.tooltips.hide();
      this.#pan = {
        target,
        last: p,
        spread: 0,
        startSpread: 0,
        zooming: false,
        vx: 0,
        vy: 0,
        at: performance.now(),
        mouse: false,
      };
      const left = this.#applyPan(dx - used.x, dy - used.y);
      // Whose gesture it is follows the way the finger is mostly going. A
      // grid that scrolls only sideways takes a sliver of a vertical slide,
      // and calling that the application's would keep a zoomed page from
      // panning over every wide grid on it.
      const along = Math.abs(dy) >= Math.abs(dx) ? dy - left.y : dx - left.x;
      this.#touchClaim = along !== 0 ? "claimed" : "declined";
      if (this.#touchClaim === "declined" && this.yieldDeclinedTouches) this.#pan = null;
      return true;
    }
    return false;
  }

  /**
   * Turn a mouse drag into a pan, for the controls that ask for one.
   *
   * A finger scrolls whatever is under it; a mouse must not, or dragging
   * across a list would rubber-band it instead of selecting rows. The
   * exception is a control whose content is meant to be dragged — a carousel
   * — which says so through `pansWithMouse`. The walk starts at the control
   * the press landed on, so a swipe works anywhere over a page rather than
   * only over the bare parts of one, and the claiming control becomes the
   * pan's target so the drag chains no further out than it asked to.
   *
   * The press that started it never completes: a drag is not a click.
   */
  #claimAsDrag(p: Point, e: PointerEvent): boolean {
    const pressed = this.#pressedControl;
    if (!pressed || e.pointerType === "touch") return false;
    if (this.#pressedButton !== 0 || (e.buttons & 1) === 0) return false;
    const from = this.#pressedAt;
    if (distance(p, from) < TOUCH_SLOP) return false;
    // Same exclusion the scroll claim makes: a splitter or a scroll thumb is
    // already having its own drag, whatever it is sitting inside.
    if (pressed.handlesDragAt(pressed.fromScreen(from))) return false;
    if (this.#underBusyScrim(pressed, from)) return false;

    // The content follows the pointer, so the pan goes the other way.
    const dx = from.x - p.x;
    const dy = from.y - p.y;
    for (let c: Control | null = pressed; c; c = c.parent) {
      if (!c.effectivelyEnabled || !c.pansWithMouse) continue;
      const used = c.panBy(dx, dy);
      if (used.x === 0 && used.y === 0) continue;
      pressed.setPressed(false);
      this.#pressedControl = null;
      this.#lastClickTarget = null;
      this.#desktop.tooltips.hide();
      this.#pan = {
        target: c,
        last: p,
        spread: 0,
        startSpread: 0,
        zooming: false,
        vx: 0,
        vy: 0,
        at: performance.now(),
        mouse: true,
      };
      this.#applyPan(dx - used.x, dy - used.y);
      return true;
    }
    return false;
  }

  /** True when this touch has been handed to the browser; drops its press. */
  #yielded(e: PointerEvent): boolean {
    if (!this.yieldDeclinedTouches || e.pointerType !== "touch") return false;
    if (this.#touchClaim !== "declined") return false;
    this.#pendingDown = null;
    this.#cancelLongPress();
    return true;
  }

  /** Dispatch a press that was held back, if there is one. */
  #flushPendingDown(): void {
    const pending = this.#pendingDown;
    if (!pending) return;
    this.#pendingDown = null;
    this.#cancelLongPress();
    this.#press(pending);
  }

  #cancelLongPress(): void {
    if (this.#longPressTimer === null) return;
    clearTimeout(this.#longPressTimer);
    this.#longPressTimer = null;
  }

  /**
   * A finger held still long enough is the phone's right-click.
   *
   * The press goes through first, so the menu opens against a window that has
   * been raised and a row that has been selected, exactly as a right-click
   * does. It carries the secondary button, which is what keeps a text box from
   * dropping its caret and a title bar from starting a drag under the menu.
   */
  #fireLongPress = (): void => {
    this.#longPressTimer = null;
    const pending = this.#pendingDown;
    if (!pending) return;
    const p = this.#pendingAt;

    // A finger resting on something whose whole purpose is the drag — a
    // scroll thumb, a splitter, a column divider — is lining up that drag,
    // not asking for a menu. Same test the scroll claim uses.
    const under = this.#desktop.hitTestScreen(p);
    if (under && under.handlesDragAt(under.fromScreen(p))) return;

    this.#pendingDown = null;
    this.#longPressed = true;
    const source = secondaryOf(pending);
    this.#press(pending, source);

    // No control took the press: it was swallowed by a popup dismissal or a
    // modal, or it landed on bare desktop, which raises its own menu.
    const pressed = this.#pressedControl;
    if (!pressed) return;
    // Closed out here rather than on the release, both so the control does not
    // sit pressed under the open menu and so the lift finds nothing to click.
    this.#pressedControl = null;
    pressed.setPressed(false);
    pressed.emit("contextmenu", this.#argsFor(pressed, p, source));
  };

  #press(e: PointerEvent, source: MouseSource = e): void {
    this.#dispatchPointerDown(e, source);
    // Place DOM focus once the press has settled who has Webwin focus, and do
    // it here rather than inside the dispatch so it happens on every path out.
    // Still inside the user gesture, which is the only time a browser will
    // raise an on-screen keyboard for a programmatic focus.
    this.#desktop.keyboard.sync();
  }

  #onPointerDown = (e: PointerEvent): void => {
    // Touching the screen catches a coasting list, as it should.
    this.#desktop.stopFling();
    this.#longPressed = false;
    this.#contacts.set(e.pointerId, this.#toCanvas(e));
    if (this.#contacts.size >= 2) {
      // A second finger means this was a gesture all along, so the press the
      // first one was about to make never happens, menu and all.
      this.#pendingDown = null;
      this.#cancelLongPress();
      if (!this.#pan) this.#beginPan();
      else this.#rebasePan();
      return;
    }
    // Only touch waits. A mouse cannot grow a second contact, and delaying it
    // would make the whole app feel a frame behind.
    if (e.pointerType === "touch") {
      this.#touchClaim = "deciding";
      this.#pendingDown = e;
      this.#pendingAt = this.#toCanvas(e);
      this.#cancelLongPress();
      this.#longPressTimer = setTimeout(this.#fireLongPress, LONG_PRESS_MS);
    } else {
      this.#press(e);
    }
  };

  #dispatchPointerDown(e: PointerEvent, source: MouseSource = e): void {
    const p = this.#toCanvas(e);
    this.#pointer = p;
    this.#inside = true;

    // Any press makes a tooltip stale.
    this.#desktop.tooltips.hide();

    const target = this.#target(p);
    // A press outside an open popup dismisses it and goes no further, the way
    // Windows menus swallow the click that closes them.
    if (this.#desktop.dismissPopupIfOutside(target)) return;
    if (!target) {
      this.#desktop.onBackgroundPointerDown(p, source);
      return;
    }
    if (!this.#desktop.allowsInput(target)) {
      this.#desktop.onBlockedByModal(target);
      return;
    }

    try {
      this.#canvas.setPointerCapture(e.pointerId);
    } catch {
      // Throws NotFoundError when the pointer is no longer active — a contact
      // lifted between the event being queued and handled, or a synthetic
      // event from a test. Capture is an optimization for tracking a drag
      // past the canvas edge; losing it must not abort the press.
    }
    // Bring the window forward first: a busy pane still belongs to a window
    // the user is entitled to raise.
    this.#desktop.activateFor(target);
    if (this.#underBusyScrim(target, p)) return;

    // Track click count before dispatching so handlers can read it.
    const now = performance.now();
    const isRepeat =
      this.#lastClickTarget === target &&
      now - this.#lastClickAt <= DOUBLE_CLICK_MS &&
      distance(p, this.#lastClickPos) <= DOUBLE_CLICK_SLOP;
    this.#clickCount = isRepeat ? this.#clickCount + 1 : 1;
    this.#lastClickAt = now;
    this.#lastClickPos = p;
    this.#lastClickTarget = target;

    if (!target.effectivelyEnabled) return;

    this.#pressedControl = target;
    this.#pressedButton = source.button ?? 0;
    this.#pressedAt = p;
    target.setPressed(true);
    target.emit("pointerdown", this.#argsFor(target, p, source, this.#clickCount));
  }

  #onPointerMove = (e: PointerEvent): void => {
    const p = this.#toCanvas(e);
    this.#pointer = p;
    this.#inside = true;
    if (this.#contacts.has(e.pointerId)) this.#contacts.set(e.pointerId, p);

    // A drag in flight owns the pointer outright — including from the control
    // that started it, which is usually still holding the capture it took on
    // the press. Everything below this line is for pointers that are free.
    const dragging = this.#desktop.drag;
    if (dragging) {
      if (this.#touchClaim === "deciding") this.#touchClaim = "claimed";
      dragging.moveTo(p);
      return;
    }

    if (this.#pan) {
      // A repeat from a contact already counted means the previous round is
      // as complete as it is going to get — the other finger is holding still.
      if (this.#moved.has(e.pointerId)) this.#flushGesture();
      this.#moved.add(e.pointerId);
      if (this.#moved.size >= this.#contacts.size) this.#flushGesture();
      return;
    }
    // One finger that has started moving. Before the press goes through, see
    // whether this is a scroll instead.
    if (this.#yielded(e)) return;
    if (this.#pendingDown && this.#claimAsScroll(p)) return;
    if (this.#touchClaim === "deciding" && e.pointerType === "touch") this.#touchClaim = "declined";
    if (this.#yielded(e)) return;
    this.#flushPendingDown();

    // A mouse dragging something that pans with one, once it has traveled
    // far enough to mean it. Nothing with a capture in flight: that drag has
    // already found its owner.
    if (!this.#captured && this.#claimAsDrag(p, e)) return;

    if (this.#captured) {
      const c = this.#captured;
      const local = c.fromScreen(p);
      if (!this.#underBusyScrim(c, p)) c.emit("pointermove", this.#argsFor(c, p, e));
      this.#desktop.setCursor(c.cursorAt(local));
      return;
    }

    this.#updateHover(p, e);
    const target = this.#hovered;
    if (target && target.effectivelyEnabled && !this.#underBusyScrim(target, p)) {
      target.emit("pointermove", this.#argsFor(target, p, e));
      // Again, after the handler. A control that chooses its cursor from what
      // is under the pointer — a 3D viewport hit-testing the scene, a canvas
      // with tools — has only just decided, and the cursor `#updateHover`
      // applied was the one from the previous move. Stop moving over the thing
      // and it would sit there wrong until the pointer twitched again.
      // `setCursor` early-outs when nothing changed, so this costs nothing.
      this.#desktop.setCursor(target.cursorAt(target.fromScreen(p)));
    }
    // The handler may have just begun a drag — an item slot does, from its own
    // move — and `beginDrag` hid the tip. Asking again now would bring it back
    // at once (tips are warm right after a hide), and every move from here goes
    // to the drag, so nothing would hide it again until the drop.
    if (this.#desktop.drag) return;
    this.#desktop.tooltips.pointerMoved(target, p);
  };

  #onPointerUp = (e: PointerEvent): void => {
    const p = this.#toCanvas(e);
    this.#pointer = p;
    this.#contacts.delete(e.pointerId);
    if (this.#contacts.size === 0) this.#touchClaim = null;
    if (this.#canvas.hasPointerCapture(e.pointerId)) {
      this.#canvas.releasePointerCapture(e.pointerId);
    }

    // Releasing over a drop target is the whole point of the drag, and it is
    // emphatically not a click on whatever is underneath.
    const dragging = this.#desktop.drag;
    if (dragging) {
      dragging.drop(p);
      if (this.#captured) this.releaseCapture(this.#captured);
      if (this.#pressedControl) {
        this.#pressedControl.setPressed(false);
        this.#pressedControl = null;
      }
      return;
    }

    if (this.#pan) {
      // Hold the gesture while two or more fingers remain; the rest of the
      // release path is for presses, and this was never one.
      if (this.#contacts.size >= 2) this.#rebasePan();
      else this.#endPan();
      return;
    }
    // A tap: the press and the release arrive together.
    this.#flushPendingDown();

    // The lift that ends a long press is not a click: the menu it asked for
    // is open, and the press it belonged to was closed out when it opened.
    if (this.#longPressed) {
      this.#longPressed = false;
      if (this.#captured) this.releaseCapture(this.#captured);
      return;
    }

    const captured = this.#captured;
    if (captured) {
      if (!this.#underBusyScrim(captured, p)) {
        captured.emit("pointerup", this.#argsFor(captured, p, e));
      }
      // The capture owner decides when to release; if it did not, do it here.
      if (this.#captured === captured) this.releaseCapture(captured);
      this.#finishPress(p, e);
      return;
    }

    const target = this.#desktop.hitTestScreen(p);
    if (
      target &&
      target.effectivelyEnabled &&
      this.#desktop.allowsInput(target) &&
      !this.#underBusyScrim(target, p)
    ) {
      target.emit("pointerup", this.#argsFor(target, p, e));
    }
    this.#finishPress(p, e);
  };

  /** Emit click / doubleclick when press and release land on the same control. */
  #finishPress(p: Point, e: PointerEvent): void {
    const pressed = this.#pressedControl;
    this.#pressedControl = null;
    if (!pressed) return;
    pressed.setPressed(false);
    if (e.button !== this.#pressedButton) return;

    const releaseTarget = this.#captured ?? this.#desktop.hitTestScreen(p);
    const onTarget = releaseTarget === pressed || (this.#captured === pressed);
    if (!onTarget || !pressed.effectivelyEnabled) return;
    if (this.#underBusyScrim(pressed, p)) return;

    const args = this.#argsFor(pressed, p, e, this.#clickCount);
    if (e.button === 2) {
      pressed.emit("contextmenu", args);
      return;
    }
    pressed.emit("click", args);
    if (this.#clickCount === 2) {
      pressed.emit("doubleclick", this.#argsFor(pressed, p, e, 2));
    }
  }

  #onPointerCancel = (e: PointerEvent): void => {
    this.#contacts.delete(e.pointerId);
    if (this.#contacts.size === 0) this.#touchClaim = null;
    // A canceled pointer cannot say where it was dropped, so it was not.
    this.#desktop.drag?.cancel();
    // A canceled press never happened, so drop it rather than flushing.
    if (this.#pendingDown?.pointerId === e.pointerId) this.#pendingDown = null;
    this.#cancelLongPress();
    this.#longPressed = false;
    if (this.#pan && this.#contacts.size < 2) this.#endPan();
    if (this.#pressedControl) {
      this.#pressedControl.setPressed(false);
      this.#pressedControl = null;
    }
    this.#captured = null;
  };

  #onPointerLeave = (): void => {
    this.#inside = false;
    this.#desktop.tooltips.hide();
    if (this.#captured) return;
    if (this.#hovered) {
      this.#hovered.setHovered(false);
      this.#hovered.emit(
        "pointerleave",
        makeMouseArgs(0, 0, this.#pointer, {}, 0),
      );
      this.#hovered = null;
    }
    this.#desktop.setCursor("default");
  };

  #onContextMenu = (e: MouseEvent): void => {
    // Suppress the browser menu; controls get a "contextmenu" control event.
    e.preventDefault();
    // A long press on a phone can reach the browser's own recognizer before
    // our timer, and the pointercancel it sends afterwards would throw the
    // press away. Whichever arrives first raises the menu; the other finds
    // nothing pending and does nothing.
    if (this.#pendingDown) {
      this.#cancelLongPress();
      this.#fireLongPress();
    }
  };

  #onWheel = (e: WheelEvent): void => {
    this.#desktop.tooltips.hide();
    const p = this.#toCanvas(e);
    this.#pointer = p;
    const target = this.#captured ?? this.#desktop.hitTestScreen(p);
    if (!target || !this.#desktop.allowsInput(target)) return;
    // Scrolling a busy pane is as unwelcome as clicking it. Swallowed rather
    // than bubbled, so it does not scroll whatever is behind either.
    if (this.#underBusyScrim(target, p)) {
      e.preventDefault();
      return;
    }

    const factor =
      e.deltaMode === 1 ? LINE_HEIGHT_PX : e.deltaMode === 2 ? PAGE_HEIGHT_PX : 1;

    // Bubble up the parent chain until something claims it.
    let control: Control | null = target;
    while (control) {
      if (control.effectivelyEnabled) {
        const local = control.fromScreen(p);
        const args: WheelEventArgs = {
          ...makeMouseArgs(local.x, local.y, p, e as unknown as { button?: number }),
          deltaX: e.deltaX * factor,
          deltaY: e.deltaY * factor,
        };
        control.emit("wheel", args);
        if (args.handled) {
          e.preventDefault();
          return;
        }
      }
      control = control.parent;
    }
    // Ctrl+wheel would page-zoom the browser; never let that through.
    if (e.ctrlKey) e.preventDefault();
  };

  // --- hover -------------------------------------------------------------

  #updateHover(p: Point, e?: PointerEvent): void {
    const raw = this.#desktop.hitTestScreen(p);
    const next = raw && this.#desktop.allowsInput(raw) ? raw : null;
    if (next === this.#hovered) {
      if (next) this.#desktop.setCursor(next.cursorAt(next.fromScreen(p)));
      return;
    }
    const previous = this.#hovered;
    this.#hovered = next;
    if (previous) {
      previous.setHovered(false);
      previous.emit("pointerleave", makeMouseArgs(0, 0, p, e ?? {}, 0));
    }
    if (next) {
      next.setHovered(true);
      const local = next.fromScreen(p);
      next.emit("pointerenter", makeMouseArgs(local.x, local.y, p, e ?? {}, 0));
    }
    this.#desktop.setCursor(next ? next.cursorAt(next.fromScreen(p)) : "default");
  }

  /** Recompute hover after the tree changed under a stationary pointer. */
  refreshHover(): void {
    if (this.#inside && !this.#captured) this.#updateHover(this.#pointer);
  }

  dispose(): void {
    this.#cancelLongPress();
    const canvas = this.#canvas;
    canvas.removeEventListener("pointerdown", this.#onPointerDown);
    canvas.removeEventListener("pointermove", this.#onPointerMove);
    canvas.removeEventListener("pointerup", this.#onPointerUp);
    canvas.removeEventListener("pointercancel", this.#onPointerCancel);
    canvas.removeEventListener("pointerleave", this.#onPointerLeave);
    canvas.removeEventListener("contextmenu", this.#onContextMenu);
    canvas.removeEventListener("wheel", this.#onWheel);
  }
}

/**
 * Fold one movement sample into a running speed, in units per second.
 *
 * Smoothed rather than taken raw, because the throw should follow the hand
 * and the very last event before a finger lifts is usually a stutter or a
 * dead stop. A gap longer than `MAX_SAMPLE_GAP` means the finger paused, and
 * a pause means no throw at all — otherwise a stale speed gets flung the
 * moment the finger leaves.
 */
export function smoothVelocity(previous: number, delta: number, dt: number): number {
  if (dt <= 0 || dt > MAX_SAMPLE_GAP) return 0;
  const k = VELOCITY_SMOOTHING;
  return previous * (1 - k) + (delta / dt) * k;
}
